// Third-party
import React from 'react';

// Components
import { SecondaryNavigation } from '@/views/components/navigation';

// Utils
import { ADVANCED_SEARCH_OPTIONS, SCREENER_TAB_MODES } from '@/utils/constants';
import { formatMessage } from '@/utils/i18n';
import { userHasPermission, PERMISSIONS } from '@/utils/permissions';

// Types
import User from '@/types/user';
import Nullable from '@/types/nullable';
import NavigationItem from '@/types/navigation-item';

type Props = {
  user: Nullable<User> | undefined;
  mode?: (typeof SCREENER_TAB_MODES)[keyof typeof SCREENER_TAB_MODES];
  selectedId?: ADVANCED_SEARCH_OPTIONS; // For controlled mode
  onSelect?: (instrument: ADVANCED_SEARCH_OPTIONS) => void;
  basePath?: string;
};

const ScreenerInstrumentTabs: React.FC<Props> = ({ user, mode = SCREENER_TAB_MODES.URL, selectedId, onSelect, basePath = '/search' }) => {
  const getTabOptions = (): NavigationItem[] => {
    const isUrlMode = mode === SCREENER_TAB_MODES.URL;

    const tabs: NavigationItem[] = [
      {
        id: ADVANCED_SEARCH_OPTIONS.FUNDS,
        value: isUrlMode ? `${basePath}/funds` : '#',
        label: formatMessage('Screener.Tabs.Funds'),
        exact: isUrlMode,
        subValues: isUrlMode ? [`${basePath}`] : undefined,
        callback: !isUrlMode ? () => onSelect?.(ADVANCED_SEARCH_OPTIONS.FUNDS) : undefined,
      },
      {
        id: ADVANCED_SEARCH_OPTIONS.BENCHMARKS,
        value: isUrlMode ? `${basePath}/benchmarks` : '#',
        label: formatMessage('Screener.Tabs.Benchmarks'),
        exact: isUrlMode,
        hidden: !userHasPermission(user, PERMISSIONS.BENCHMARKS),
        callback: !isUrlMode ? () => onSelect?.(ADVANCED_SEARCH_OPTIONS.BENCHMARKS) : undefined,
      },
      {
        id: ADVANCED_SEARCH_OPTIONS.STOCKS,
        value: isUrlMode ? `${basePath}/stocks` : '#',
        label: formatMessage('Screener.Tabs.Stocks'),
        exact: isUrlMode,
        hidden: !userHasPermission(user, PERMISSIONS.SCREENER_STOCKS),
        callback: !isUrlMode ? () => onSelect?.(ADVANCED_SEARCH_OPTIONS.STOCKS) : undefined,
      },
      {
        id: ADVANCED_SEARCH_OPTIONS.BONDS,
        value: isUrlMode ? `${basePath}/bonds` : '#',
        label: formatMessage('Screener.Tabs.Bonds'),
        exact: isUrlMode,
        hidden: !userHasPermission(user, PERMISSIONS.SCREENER_BONDS),
        callback: !isUrlMode ? () => onSelect?.(ADVANCED_SEARCH_OPTIONS.BONDS) : undefined,
      },
    ];

    return tabs;
  };

  return (
    <SecondaryNavigation
      className="screener-instrument-tabs secondary-navigation-v2"
      items={getTabOptions()}
      selectedId={mode === SCREENER_TAB_MODES.CONTROLLED ? selectedId : undefined}
    />
  );
};

export default ScreenerInstrumentTabs;
